import { Star, Quote } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card } from "@/components/ui/card";
import CountUpNumber from "@/components/CountUpNumber";
const TestimonialsSection = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      name: t('testimonials.client1.name'),
      role: t('testimonials.client1.role'),
      text: t('testimonials.client1.text'),
      rating: 5
    },
    {
      name: t('testimonials.client2.name'),
      role: t('testimonials.client2.role'),
      text: t('testimonials.client2.text'),
      rating: 5
    },
    {
      name: t('testimonials.client3.name'),
      role: t('testimonials.client3.role'),
      text: t('testimonials.client3.text'),
      rating: 4
    },
    {
      name: t('testimonials.client4.name'),
      role: t('testimonials.client4.role'),
      text: t('testimonials.client4.text'),
      rating: 5
    },
    {
      name: t('testimonials.client5.name'),
      role: t('testimonials.client5.role'),
      text: t('testimonials.client5.text'),
      rating: 5
    },
    {
      name: t('testimonials.client6.name'),
      role: t('testimonials.client6.role'),
      text: t('testimonials.client6.text'),
      rating: 5
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-full mb-4">
            <Star className="w-3 h-3 text-accent fill-accent" />
            <span className="text-xs font-modern font-semibold text-accent uppercase tracking-wider">
              {t('testimonials.badge')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-outline text-foreground mb-3 uppercase tracking-tight leading-tight" style={{ fontWeight: 900 }}>
            <span className="relative inline-block">
              <span className="relative z-10">{t('testimonials.title')}</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-accent/30 -z-0"></span>
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto font-modern font-medium leading-relaxed">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6 max-w-7xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index}
              className="group relative bg-card border border-border rounded-2xl p-6 hover:border-accent/50 transition-all duration-500 hover:shadow-xl hover:shadow-accent/10 hover:-translate-y-1"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Quote icon */}
              <div className="absolute top-4 right-4 opacity-10">
                <Quote className="w-12 h-12 text-accent" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${i < testimonial.rating ? 'text-accent fill-accent' : 'text-accent'}`}
                  />
                ))}
              </div>

              {/* Testimonial text */}
              <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-modern relative z-10">
                "{testimonial.text}"
              </p>

              {/* Client info */}
              <div className="relative z-10">
                <p className="font-modern font-bold text-foreground text-sm">{testimonial.name}</p>
                <p className="font-modern text-muted-foreground text-xs">{testimonial.role}</p>
              </div>

              {/* Hover gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-accent/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </Card>
          ))}
        </div>

        {/* Bottom stats */}
        <div className="mt-8 md:mt-12 lg:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
          <div className="text-center">
            <CountUpNumber 
              end={150} 
              suffix="+" 
              duration={2000}
              className="text-3xl md:text-4xl font-modern font-black text-accent mb-1 block"
            />
            <div className="text-sm text-muted-foreground font-modern">{t('testimonials.stats.projects')}</div>
          </div>
          <div className="text-center">
            <CountUpNumber 
              end={98} 
              suffix="%" 
              duration={2000}
              className="text-3xl md:text-4xl font-modern font-black text-accent mb-1 block"
            />
            <div className="text-sm text-muted-foreground font-modern">{t('testimonials.stats.satisfaction')}</div>
          </div>
          <div className="text-center">
            <CountUpNumber 
              end={4.9} 
              decimals={1}
              duration={2000}
              className="text-3xl md:text-4xl font-modern font-black text-accent mb-1 block"
            />
            <div className="text-sm text-muted-foreground font-modern">{t('testimonials.stats.rating')}</div>
          </div>
          <div className="text-center">
            <CountUpNumber 
              end={24} 
              suffix="h" 
              duration={2000}
              className="text-3xl md:text-4xl font-modern font-black text-accent mb-1 block"
            />
            <div className="text-sm text-muted-foreground font-modern">{t('testimonials.stats.response')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
